// @ts-strict-ignore
import { useCegidInvoicePdf } from "@dashboard/cegid/invoicePdf";
import { cegidInvoicePdfMessages } from "@dashboard/cegid/messages";
import { DashboardCard } from "@dashboard/components/Card";
import Date from "@dashboard/components/Date";
import { InvoiceFragment } from "@dashboard/graphql";
import { buttonMessages } from "@dashboard/intl";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import { FormattedMessage, useIntl } from "react-intl";

// The "Generate invoice" action now lives in the order progress stepper so all
// order-progression controls sit in one guided place.

interface OrderInvoiceListProps {
  invoices: InvoiceFragment[];
  onInvoiceClick: (invoiceId: string) => void;
  onInvoiceSend: (invoiceId: string) => void;
}

const OrderInvoiceList = ({ invoices, onInvoiceSend }: OrderInvoiceListProps) => {
  const intl = useIntl();
  const generatedInvoices = invoices?.filter(invoice => invoice.status === "SUCCESS");
  // CEGID invoices are archived server side; open/print that copy instead of
  // CEGID's short-lived print link.
  const { run: runPdf, pendingInvoiceId } = useCegidInvoicePdf();

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage({
            id: "Gzg8hy",
            defaultMessage: "Invoices",
            description: "section header",
          })}
        </DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content>
        {!generatedInvoices ? (
          <Skeleton />
        ) : !generatedInvoices?.length ? (
          <Text color="default2">
            <FormattedMessage id="hPB89Y" defaultMessage="No invoices to be shown" />
          </Text>
        ) : (
          <Box display="flex" flexDirection="column">
            {generatedInvoices.map((invoice, index) => {
              const isPending = pendingInvoiceId === invoice.id;

              return (
                <Box
                  key={invoice.id}
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  paddingY={3}
                  borderTopStyle={index > 0 ? "solid" : undefined}
                  borderTopWidth={index > 0 ? 1 : undefined}
                  borderColor="default1"
                  data-test-id="invoice-row"
                >
                  <Box
                    as="button"
                    type="button"
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap={0.5}
                    padding={0}
                    borderWidth={0}
                    backgroundColor="transparent"
                    cursor="pointer"
                    textAlign="left"
                    onClick={() => runPdf(invoice.id, "open")}
                    data-test-id="invoice-open"
                  >
                    <Text fontWeight="medium">
                      <FormattedMessage
                        id="m6IBe5"
                        defaultMessage="Invoice"
                        description="invoice number prefix"
                      />{" "}
                      {invoice.number}
                    </Text>
                    <Text size={2} color="default2">
                      <FormattedMessage
                        id="F0AXNs"
                        defaultMessage="created"
                        description="invoice create date prefix"
                      />{" "}
                      <Date date={invoice.createdAt} plain />
                    </Text>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    <Button
                      size="small"
                      variant="secondary"
                      disabled={isPending}
                      onClick={() => runPdf(invoice.id, "print")}
                      data-test-id="invoice-print"
                    >
                      {intl.formatMessage(cegidInvoicePdfMessages.print)}
                    </Button>
                    <Button
                      size="small"
                      variant="secondary"
                      disabled={isPending}
                      onClick={() => runPdf(invoice.id, "download")}
                      data-test-id="invoice-download"
                    >
                      {intl.formatMessage(cegidInvoicePdfMessages.download)}
                    </Button>
                    {onInvoiceSend && (
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => onInvoiceSend(invoice.id)}
                        data-test-id="invoice-send"
                      >
                        <FormattedMessage {...buttonMessages.send} />
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DashboardCard.Content>
    </DashboardCard>
  );
};

OrderInvoiceList.displayName = "OrderInvoiceList";
export default OrderInvoiceList;
